"use client"

import { useTranslations } from "next-intl"
import { useEffect, useCallback } from "react"
import { useLocalStorage } from "usehooks-ts"

import type { DishWithCategory } from "@/app/[locale]/merchant/page.types"
import { CONST } from "@/constants"
import { PriceSharedUtils } from "@/utils/shared/PriceSharedUtils"

import { useOngoingOrder } from "./customer/useOngoingOrder"
import { useToast } from "./use-toast"
import { usePendingOrder } from "./customer/usePendingOrder"
import { CustomerOrderDishAddOnCreateSchema } from "@/lib/zenstack/zod/models"
import { z } from "zod"
import { v4 as uuidv4 } from "uuid"
import { useQueryState } from "nuqs"
import { usePathname, useRouter } from "@/i18n/routing"
import { ROUTES } from "@/routes"

export interface CartContent {
  merchantId: string
  merchantName: string
  merchantDefaultDiscount: number
  cartType: "DINE_IN" | "TAKE_OUT"
  items: CartItem[]
}

export interface CartItem {
  id: string
  dish: DishWithCategory
  quantity: number
  addOns: CartItemAddOns[]
}
const cartItemAddOnsSchema = CustomerOrderDishAddOnCreateSchema.pick({
  id: true,
  nameEN: true,
  nameFR: true,
  descriptionEN: true,
  descriptionFR: true,
  priceCents: true,
  quantity: true,
  categoryType: true,
  categoryNameEN: true,
  categoryNameFR: true,
}).extend({
  addOnCategoryId: z.string(),
})

export type CartItemAddOns = z.infer<typeof cartItemAddOnsSchema>

export const EDIT_CART_ITEM_QUERY_PARAM = "edit_cart_item_id"

export function useCart() {
  const { ongoingOrder } = useOngoingOrder()
  const { pendingOrder } = usePendingOrder()
  const router = useRouter()
  const pathname = usePathname()

  const { toast } = useToast()
  const t = useTranslations("customer.orders")

  const [cart, setCart] = useLocalStorage<CartContent | null>(CONST.LOCAL_STORAGE_KEYS.CART(), null)

  const [, setEditCartItemId] = useQueryState(EDIT_CART_ITEM_QUERY_PARAM)

  useEffect(() => {
    if (cart?.items.length === 0) {
      setCart(null)
    }
  }, [cart])

  const checkMerchantId = useCallback(
    (dish: DishWithCategory) => {
      if (ongoingOrder?.merchantId && cart?.merchantId && ongoingOrder.merchantId !== cart.merchantId) {
        toast({
          title: t("cart.different_merchant.title"),
          description: t("cart.different_merchant.description"),
          variant: "destructive",
        })
        return false
      }
      return true
    },
    [ongoingOrder?.merchantId, cart?.merchantId, toast, t]
  )

  const checkOrderIsCheckingOut = useCallback(() => {
    if (ongoingOrder?.initiateCheckout) {
      toast({
        title: t("cart.checkout_in_progress.title"),
        description: t("cart.checkout_in_progress.description"),
        variant: "destructive",
      })
      return false
    }
    return true
  }, [ongoingOrder?.initiateCheckout, toast, t])

  const addToCart = useCallback(
    (dish: DishWithCategory, quantity: number, addOns: CartItemAddOns[], setNewCart?: boolean) => {
      if (!dish.category.merchantId) {
        throw new Error("Dish has no merchantId")
      }
      if (!checkMerchantId(dish)) {
        return
      }
      if (pendingOrder) {
        return
      }
      if (!checkOrderIsCheckingOut()) {
        return
      }
      const _newCart = {
        merchantId: dish.category.merchantId,
        merchantName: dish.merchantName,
        merchantDefaultDiscount: dish.merchantDefaultDiscount,
        cartType: "DINE_IN",
        items: [],
      } satisfies CartContent
      const newCart = setNewCart ? _newCart : (cart ?? _newCart)
      if (newCart.merchantId !== dish.category.merchantId) {
        throw new Error("Dish is from a different merchant")
      }

      // Check if the item already exists in the cart
      // Compare dish and addOns
      const isItemExistIdx = newCart.items.findIndex(
        (i) =>
          i.dish.id === dish.id &&
          i.addOns.length === addOns.length &&
          i.addOns.every((a) => addOns.some((a2) => a2.id === a.id))
      )
      if (isItemExistIdx !== -1) {
        const item = newCart.items[isItemExistIdx]
        const newQuantity = item.quantity + quantity
        const newItem = {
          ...item,
          quantity: newQuantity,
        }
        setCart({
          ...newCart,
          items: [...newCart.items.slice(0, isItemExistIdx), newItem, ...newCart.items.slice(isItemExistIdx + 1)],
        })
      } else {
        setCart({
          ...newCart,
          items: [...newCart.items, { id: uuidv4(), dish, quantity: quantity, addOns }],
        })
      }
    },
    [cart, checkMerchantId, checkOrderIsCheckingOut, pendingOrder, setCart]
  )

  const updateCartItem = useCallback(
    (item: CartItem, updateBy: "byId" | "byDish") => {
      if (!cart) return
      if (pendingOrder) {
        return
      }
      if (!checkOrderIsCheckingOut()) {
        return
      }
      const { dish, quantity, addOns } = item
      if (updateBy === "byId") {
        const cartItem = cart.items.find((i) => i.id === item.id)
        if (cartItem) {
          setCart({
            ...cart,
            items: cart.items.map((i) => (i.id === cartItem.id ? { ...i, dish, quantity, addOns } : i)),
          })
        }
      } else {
        setCart({
          ...cart,
          items: cart.items.map((i) => (i.dish.id === dish.id ? { ...i, dish, quantity, addOns } : i)),
        })
      }
    },
    [cart, pendingOrder, checkOrderIsCheckingOut, setCart]
  )

  const deleteFromCart = useCallback(
    (item: CartItem, deleteBy: "byId" | "byDish") => {
      if (!cart) return
      if (pendingOrder) {
        return
      }
      if (deleteBy === "byId") {
        setCart({ ...cart, items: cart.items.filter((i) => i.id !== item.id) })
      } else {
        setCart({ ...cart, items: cart.items.filter((i) => i.dish.id !== item.dish.id) })
      }
    },
    [cart, pendingOrder, setCart]
  )

  const incrementInCart = useCallback(
    ({ quantity, id, dishId }: { quantity: number; id?: string; dishId?: string }) => {
      if (!cart) return
      if (pendingOrder) {
        return
      }
      if (!checkOrderIsCheckingOut()) {
        return
      }
      let cartItem: CartItem | undefined
      if (id) {
        cartItem = cart.items.find((i) => i.id === id)
      } else {
        cartItem = cart.items.find((i) => i.dish.id === dishId)
      }
      if (cartItem) {
        setCart({
          ...cart,
          items: cart.items.map((i) => (i.id === cartItem.id ? { ...i, quantity: cartItem.quantity + quantity } : i)),
        })
      }
    },
    [cart, pendingOrder, checkOrderIsCheckingOut, setCart]
  )

  const decrementInCart = useCallback(
    ({ quantity, id, dishId }: { quantity: number; id?: string; dishId?: string }) => {
      if (!cart) return
      if (pendingOrder) {
        return
      }

      let cartItem: CartItem | undefined
      if (id) {
        cartItem = cart.items.find((i) => i.id === id)
      } else {
        cartItem = cart.items.find((i) => i.dish.id === dishId)
      }
      if (cartItem) {
        const cartQuantity = cartItem?.quantity ?? 0
        const newQuantity = Math.max(0, cartQuantity - quantity)
        if (newQuantity === 0) {
          deleteFromCart(cartItem, "byId")
          return
        }
        setCart({
          ...cart,
          items: cart.items.map((i) => (i.id === cartItem.id ? { ...i, quantity: newQuantity } : i)),
        })
      }
    },
    [cart, pendingOrder, deleteFromCart, setCart]
  )

  const clearCart = useCallback(
    (dish?: DishWithCategory) => {
      if (dish) {
        addToCart(dish, 1, [], true)
      } else {
        setCart(null)
      }
    },
    [addToCart, setCart]
  )

  const getCartMerchantId = useCallback(() => {
    if (!cart) return null
    return cart.merchantId
  }, [cart])

  const getCartMerchantName = useCallback(() => {
    if (!cart) return null
    return cart.merchantName
  }, [cart])

  const getCartMerchantDefaultDiscount = useCallback(() => {
    if (!cart) return null
    return cart.merchantDefaultDiscount
  }, [cart])

  const getCartItems = useCallback(() => {
    if (!cart) return []
    return cart.items
  }, [cart])

  const getItemsByDish = useCallback(
    (dish: DishWithCategory) => {
      if (!cart) return []
      return cart.items.filter((i) => i.dish.id === dish.id)
    },
    [cart]
  )

  const getItemById = useCallback(
    (id: string) => {
      if (!cart) return null
      return cart.items.find((i) => i.id === id)
    },
    [cart]
  )

  const getTotalQuantity = useCallback(() => {
    if (!cart) return 0
    return cart.items.reduce((total, item) => {
      return total + item.quantity
    }, 0)
  }, [cart])

  const getCartType = useCallback(() => {
    if (!cart) return "DINE_IN"
    return cart.cartType
  }, [cart])
  const setCartType = useCallback(
    (cartType: "DINE_IN" | "TAKE_OUT") => {
      if (!cart) return
      setCart({ ...cart, cartType })
    },
    [cart, setCart]
  )

  const calculateTotal = useCallback(
    (priceType: "effectivePrice" | "price") => {
      if (!cart) return 0
      const totalCents = PriceSharedUtils.calculateTotalAmountsCents(
        cart.items.map((i) => ({
          priceCents: i.dish.priceCents,
          discount: i.dish.discount,
          quantity: i.quantity,
          addOns: i.addOns.map((a) => ({
            priceCents: a.priceCents,
            quantity: a.quantity,
            discount: cart.merchantDefaultDiscount,
          })),
        })),
        priceType
      )
      return PriceSharedUtils.getDollarsPrice(totalCents)
    },
    [cart]
  )

  const calculateTotalEffectivePriceDollars = useCallback(() => calculateTotal("effectivePrice"), [calculateTotal])
  const calculateTotalPriceDollars = useCallback(() => calculateTotal("price"), [calculateTotal])

  const navigateToEditDish = useCallback(
    (item: CartItem) => {
      if (!cart) return
      if (pathname.includes(cart.merchantId)) {
        setEditCartItemId(item.id)
      } else {
        router.push(`${ROUTES.merchantPublic(cart.merchantId)}?${EDIT_CART_ITEM_QUERY_PARAM}=${item.id}`)
      }
    },
    [cart, pathname, router, setEditCartItemId]
  )

  return {
    cart,
    updateCartItem,
    addToCart,
    deleteFromCart,
    clearCart,
    calculateTotalEffectivePriceDollars,
    calculateTotalPriceDollars,
    getItemById,
    getItemsByDish,
    getTotalQuantity,
    incrementInCart,
    decrementInCart,
    getCartMerchantId,
    getCartItems,
    getCartMerchantName,
    getCartMerchantDefaultDiscount,
    navigateToEditDish,
    getCartType,
    setCartType,
  }
}
